// Les téléphones par QR code (D-167) : les appels à la base et le temps réel. La base fait foi : un geste n'existe qu'une
// fois écrit (game_live_act, à l'heure de la base, sans doublon) ; la tablette relit les gestes dans l'ordre, les applique,
// publie la partie ; les téléphones la relisent. Le temps réel de Supabase (un canal « broadcast » au nom secret de la
// partie) ne fait que PRÉVENIR plus vite : s'il ne s'ouvre pas (serveur sans temps réel, réseau qui bloque), la tablette
// et les téléphones relisent la base plus souvent, et tout marche pareil.
import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from './supabase';
import { ClockSync, type CoachKind, type LiveKind, type LiveView, type TeamKind } from '../domain/game-live';

const db = () => {
  if (!supabase) throw new Error('Base non configurée');
  return supabase;
};
async function rpc<T>(fn: string, args: Record<string, unknown>, clock?: ClockSync): Promise<T> {
  const sent = Date.now();
  const { data, error } = await db().rpc(fn, args);
  if (error) throw Object.assign(new Error(error.message), { code: error.code });
  const recv = Date.now();
  const now = (data as { now?: unknown } | null)?.now;
  if (clock && typeof now === 'number') clock.add(sent, recv, now);
  return data as T;
}
const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : 'xxxxxxxx-xxxx-4xxx-8xxx-xxxxxxxxxxxx'.replace(/x/g, () => Math.floor(Math.random() * 16).toString(16)));

// -----------------------------------------------------------------------------------------------------------------
// Le canal de temps réel : prévenir, rien de plus
// -----------------------------------------------------------------------------------------------------------------
export interface Pinger { send(event: string, payload: Record<string, unknown>): void; close(): void; readonly live: boolean }
/** Ouvre le canal de la partie ; `onUp(false)` s'il ne s'ouvre pas en 6 s (on le referme : plus d'essais). */
export function openPinger(channel: string, onPing: (event: string, payload: Record<string, unknown>) => void, onUp: (up: boolean) => void): Pinger {
  let ch: RealtimeChannel | null = null;
  let live = false, closed = false;
  const stop = () => { if (ch && supabase) { void supabase.removeChannel(ch).catch(() => undefined); } ch = null; live = false; };
  try {
    if (!supabase) throw new Error('pas de base');
    ch = supabase.channel(`jeu-${channel}`, { config: { broadcast: { self: false, ack: false } } });
    ch.on('broadcast', { event: '*' }, (m: { event: string; payload?: Record<string, unknown> }) => onPing(m.event, m.payload ?? {}));
    ch.subscribe(status => {
      if (closed) return;
      if (status === 'SUBSCRIBED') { live = true; onUp(true); }
      else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') { stop(); onUp(false); }
    });
    setTimeout(() => { if (!live && !closed) { stop(); onUp(false); } }, 6000);
  } catch { stop(); setTimeout(() => onUp(false), 0); }
  return {
    get live() { return live; },
    send(event, payload) { if (live && ch) void ch.send({ type: 'broadcast', event, payload }).catch(() => undefined); },
    close() { closed = true; stop(); },
  };
}

// -----------------------------------------------------------------------------------------------------------------
// La tablette
// -----------------------------------------------------------------------------------------------------------------
export interface LiveSeat { seat: string; token: string; bib: string; name: string }
export interface LiveAction { id: number; seat: string; kind: TeamKind | CoachKind; payload: Record<string, unknown>; at: number }
export interface SeatState { seat: string; held: boolean; seen: number | null; since: number | null; name: string | null }

/**
 * Une partie ouverte aux téléphones, côté tablette : elle relit les gestes (dans l'ordre, une fois chacun), publie la
 * partie, dit qui est relié. L'heure de la partie est celle de la base (`clock`).
 */
export class LiveHost {
  readonly clock = new ClockSync();
  after = 0;
  seats: SeatState[] = [];
  realtime = false;
  private pinger: Pinger | null = null;
  private timer = 0;
  private pulling: Promise<void> | null = null;
  private pubTimer = 0;
  private pending: { view: LiveView; status: string } | null = null;
  private publishing = false;
  private closed = false;
  private constructor(readonly id: string, readonly channel: string, readonly places: LiveSeat[], private onActions: (a: LiveAction[]) => void, private onSeats: (s: SeatState[]) => void) {}

  static async open(o: { teamId: string; gameId: string | null; kind: LiveKind; title: string; bibs: string[]; onActions: (a: LiveAction[]) => void; onSeats: (s: SeatState[]) => void }): Promise<LiveHost> {
    const clock = new ClockSync();
    const r = await rpc<{ id: string; channel: string; seats: LiveSeat[]; now: number }>('game_live_open', { p_team: o.teamId, p_game: o.gameId, p_kind: o.kind, p_title: o.title.slice(0, 80), p_bibs: o.bibs }, clock);
    const h = new LiveHost(r.id, r.channel, r.seats, o.onActions, o.onSeats);
    h.clock.offset = clock.offset;
    h.pinger = openPinger(r.channel, (ev) => { if (ev === 'act' || ev === 'hello') void h.pull(); }, up => { h.realtime = up; h.schedule(); });
    h.schedule();
    void h.pull();
    return h;
  }
  /** Relire la base : plus souvent sans temps réel. */
  private schedule() {
    clearInterval(this.timer);
    if (this.closed) return;
    this.timer = window.setInterval(() => void this.pull(), this.realtime ? 2500 : 700);
  }
  /** Les gestes arrivés depuis la dernière lecture (et l'état des places). Une seule lecture à la fois. */
  pull(): Promise<void> {
    if (this.closed) return Promise.resolve();
    if (this.pulling) return this.pulling;
    this.pulling = (async () => {
      try {
        const r = await rpc<{ now: number; actions: LiveAction[]; seats: SeatState[] }>('game_live_pull', { p_live: this.id, p_after: this.after }, this.clock);
        const fresh = (r.actions ?? []).filter(a => a.id > this.after);
        if (fresh.length) { this.after = fresh[fresh.length - 1]!.id; this.onActions(fresh); }
        this.seats = r.seats ?? [];
        this.onSeats(this.seats);
      } catch { /* réseau : on réessaie au prochain tour */ }
      finally { this.pulling = null; }
    })();
    return this.pulling;
  }
  /** Publier la partie (la dernière photo gagne ; une à la fois), puis prévenir les téléphones. */
  publish(view: LiveView, status: 'lobby' | 'running' | 'over'): void {
    if (this.closed) return;
    this.pending = { view, status };
    if (this.pubTimer) return;
    this.pubTimer = window.setTimeout(() => { this.pubTimer = 0; void this.flush(); }, 120);
  }
  private async flush() {
    if (this.publishing || !this.pending || this.closed) return;
    const { view, status } = this.pending;
    this.pending = null;
    this.publishing = true;
    try {
      await rpc('game_live_publish', { p_live: this.id, p_state: view, p_status: status });
      this.pinger?.send('state', { seq: view.seq, view });
    } catch { this.pending ??= { view, status }; }
    finally {
      this.publishing = false;
      if (this.pending) this.pubTimer = window.setTimeout(() => { this.pubTimer = 0; void this.flush(); }, 400);
    }
  }
  /** Fermer : les jetons ne valent plus, rien n'est gardé au-delà d'un jour. */
  async close(): Promise<void> {
    if (this.closed) return;
    clearInterval(this.timer); clearTimeout(this.pubTimer);
    this.pinger?.send('state', { closed: true });
    this.closed = true;
    this.pinger?.close();
    await rpc('game_live_publish', { p_live: this.id, p_state: {}, p_status: 'closed' }).catch(() => undefined);
  }
}

// -----------------------------------------------------------------------------------------------------------------
// Les téléphones
// -----------------------------------------------------------------------------------------------------------------
export interface PhoneJoin {
  kind: LiveKind; title: string; status: string; state: unknown; now: number;
  seat: string; name: string; bib: string | null; me: 'play' | 'watch'; since: number | null;
  channel?: string; team?: string; club?: string; logo?: string | null; color?: string | null; holder_name?: string | null;
}
/** Le téléphone d'une équipe (le jeton du QR) ou celui du coach (son compte et la partie). */
export type PhoneKey = { token: string } | { live: string };

export const phoneJoin = (k: PhoneKey, device: string, clock: ClockSync, take = false) => ('token' in k
  ? rpc<PhoneJoin | null>(take ? 'game_live_take' : 'game_live_join', { p_token: k.token, p_device: device }, clock)
  : rpc<PhoneJoin | null>('game_live_coach', { p_live: k.live, p_device: device, p_take: take }, clock));
export const phoneView = (k: PhoneKey, device: string, clock: ClockSync) => ('token' in k
  ? rpc<PhoneJoin | null>('game_live_view', { p_token: k.token, p_device: device }, clock)
  : rpc<PhoneJoin | null>('game_live_coach', { p_live: k.live, p_device: device, p_take: false }, clock));
/**
 * Les jetons des équipes, pour montrer leurs QR sur le téléphone du coach (D-174 : une partie sans tablette). La table
 * n'est lisible que par le coach qui a ouvert la partie et le staff de l'équipe (RLS de D-167).
 */
export async function liveTokens(liveId: string): Promise<{ seat: string; token: string; name: string; bib: string }[]> {
  const { data, error } = await db().from('game_live_seats').select('seat, token, name, bib').eq('live_id', liveId).not('token', 'is', null).order('seat');
  if (error) throw Object.assign(new Error(error.message), { code: error.code });
  return (data ?? []) as { seat: string; token: string; name: string; bib: string }[];
}
/** Un geste : un identifiant tiré une fois, renvoyé tel quel si le réseau hésite (la base ne le compte qu'une fois). */
export async function phoneAct(k: PhoneKey, device: string, kind: TeamKind | CoachKind, payload: Record<string, unknown>, clock: ClockSync): Promise<{ id: number; at: number }> {
  const client = uuid();
  let last: unknown = null;
  for (let i = 0; i < 3; i++) {
    try {
      return 'token' in k
        ? await rpc('game_live_act', { p_token: k.token, p_device: device, p_client: client, p_kind: kind, p_payload: payload }, clock)
        : await rpc('game_live_coach_act', { p_live: k.live, p_device: device, p_client: client, p_kind: kind, p_payload: payload }, clock);
    } catch (e) {
      last = e;
      // Refusé par la base (un autre téléphone a la main, la partie n'est pas en cours) : inutile d'insister.
      if ((e as { code?: string }).code) throw e;
      await new Promise(r => setTimeout(r, 400));
    }
  }
  throw last;
}
